import { ReactNode } from 'react'

type BadgeVariant = 'primary' | 'soft' | 'secondary'
export const BadgeType = {
    Accent: 'accent',
    Success: 'success',
    Warning: 'warning',
    Danger: 'danger',
    Default: 'default',
} as const
export type BadgeType = (typeof BadgeType)[keyof typeof BadgeType]
type BadgeSize = 'sm' | 'md'

interface BadgeProps {
    variant?: BadgeVariant
    type?: BadgeType
    size?: BadgeSize
    icon?: ReactNode
    children: ReactNode
    className?: string
}

const typeVariantClasses: Record<BadgeVariant, Record<BadgeType, string>> = {
    primary: {
        accent: 'bg-blue-600 text-white',
        success: 'bg-green-600 text-white',
        warning: 'bg-yellow-500 text-white',
        danger: 'bg-red-600 text-white',
        default: 'bg-slate-600 text-white',
    },
    soft: {
        accent: 'bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700',
        success: 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-700',
        warning: 'bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-700',
        danger: 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700',
        default: 'bg-slate-50 text-slate-700 border border-slate-200 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600',
    },
    secondary: {
        accent: 'bg-slate-100 text-blue-700 dark:bg-slate-800 dark:text-blue-300',
        success: 'bg-slate-100 text-green-700 dark:bg-slate-800 dark:text-green-300',
        warning: 'bg-slate-100 text-yellow-700 dark:bg-slate-800 dark:text-yellow-300',
        danger: 'bg-slate-100 text-red-700 dark:bg-slate-800 dark:text-red-300',
        default: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    },
}

const sizeClasses: Record<BadgeSize, string> = {
    sm: 'px-2 py-0.5 text-[10px]',
    md: 'px-2.5 py-0.5 text-xs',
}

export const Badge = ({ variant = 'soft', type = 'default', size = 'md', icon, children, className = '' }: BadgeProps) => {
    return (
        <span className={`inline-flex items-center gap-1 rounded-full font-medium ${typeVariantClasses[variant][type]} ${sizeClasses[size]} ${className}`}>
            {icon}
            {children}
        </span>
    )
}
